import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {projectStoriesApi} from '../api';
import {ProjectStoriesFilter, ProjectStoriesResponse, CreateProjectStoryDTO, ProjectStoryModel} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type ProjectStoriesState = {
  getInfiniteProjectStories: UseInfiniteQueryResult<InfiniteData<ProjectStoriesResponse>, Error>;
  filters: ProjectStoriesFilter;
  updateFilter: (newFilter: Partial<ProjectStoriesFilter>) => void;
  createProjectStoryMutation: UseMutationResult<ProjectStoryModel, Error, CreateProjectStoryDTO>;
  deleteProjectStoryMutation: UseMutationResult<void, Error, string>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useProjectStories = (initialFilter: ProjectStoriesFilter = {}): ProjectStoriesState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ProjectStoriesFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteProjectStories = useInfiniteQuery<
    ProjectStoriesResponse,
    Error,
    InfiniteData<ProjectStoriesResponse>,
    ['projectStories', 'infinite', Omit<ProjectStoriesFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['projectStories', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return projectStoriesApi.getProjectStories({
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<ProjectStoriesFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createProjectStoryMutation = useMutation({
    mutationFn: projectStoriesApi.createProjectStory,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['projectStories', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Новость проекта успешно создана',
        name: 'createProjectStory',
        theme: 'success',
      });
    },
  });

  const deleteProjectStoryMutation = useMutation({
    mutationFn: projectStoriesApi.deleteProjectStory,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['projectStories', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Новость проекта успешно удалена',
        name: 'deleteProjectStory',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteProjectStories,
    filters,
    updateFilter,
    createProjectStoryMutation,
    deleteProjectStoryMutation,
  };
};
